import { Action } from '@reduxjs/toolkit';
import { RootEpic } from 'MyTypes';
import { flatten, intersection, isEmpty, isEqual, omit, xor, xorWith } from 'lodash/fp';
import { EMPTY, concat, empty, from, merge, of } from 'rxjs';
import {
    bufferCount,
    filter,
    first,
    map,
    mergeMap,
    switchMap,
    tap,
    withLatestFrom,
} from 'rxjs/operators';
import { getType, isActionOf } from 'typesafe-actions';

import { TYPE_LINE_CHART } from '@portal/common/models/chart-type';
import {
    AGE_GROUP_ID_KEY,
    LOCATION_ID_KEY,
    MAP_TYPE_ID_KEY,
    MEASURE_ID_KEY,
    METRIC_ID_KEY,
    PRIMARY_ENTITY_KEY,
    RISK_EXPOSURE_ID_KEY,
    ROUND_ID_KEY,
} from '@portal/common/models/data-key';
import {
    AllDataTypes,
    RiskExposureRecords,
    getDataTypesWithoutCondition,
} from '@portal/common/models/data-type';
import { getConditions } from '@portal/common/models/dataset';
import { isSingleYearAgeEstimatesExportEnabled as checkIfSingleYearAgeEstimatesExportEnabled } from '@portal/common/models/organization';
import { DataCondition, DataFilters, DataGranularity, DataKey } from '@portal/common/types';
import { mapConditionToValue } from '@portal/common/utility/chart-data-helpers';
import { hasErrorInRefinementFilters } from '@portal/common/utility/filters-helpers';

import {
    getAllowedChartRefinementFilters,
    getChartRefinementFiltersConfig,
    isPartOfSelectedRefinementFilters,
} from '../../components/DataExplorer/ChartSection/utils';
import { intersectRefinementFiltersWithGranularity } from '../../components/DataExplorer/utils';
import config from '../../config';
import { ANALYTICS_SOURCE_WEB } from '../../models/analytics-source';
import { LimitModal } from '../../models/data-tool';
import {
    COVID_19_EXPLORER_PATH,
    DATA_EXPLORER_PATH,
    HEALTH_THREATS_METRICS_PATH,
    MORBIDITY_EXPLORER_PATH,
} from '../../router/paths';
import {
    getDataCollectionDataTypes,
    getMergedDataCollectionGranularity,
} from '../../utility/data-loader';
import { CombinedFiltersCountOverrides } from '../../utility/data/calculate-combined-filters-count';
import { getOrganization } from '../organization/selectors';
import { changeDataCollection, setDefaultDataCollection } from '../user-settings/actions';
import {
    getSelectedDataCollectionForDataTool,
    getSelectedDataTool,
} from '../user-settings/selectors';
import {
    applyRelevantPreservedChartFilters,
    cancelSelectionCountModal,
    changeChartDimension,
    changeChartFilters,
    changeConditions,
    changeDataTypeSelection,
    changeDataTypeSelectionForSingleConditionTool,
    changeSelectedChartType,
    changeSelectedDataType,
    changeSelectedRefinementFilters,
    combinedFiltersCountEvaluated,
    confirmSelectionCountModal,
    dataSelectionValidation,
    fixMissingLoadedRefinementFilters,
    getRefinedGranularityAsync,
    getSingleYearAgeGranularitiesAsync,
    initializeDataTypeSelection,
    initializeDefaultRefinementFilters,
    openSelectionCountModal,
    queryDataAsync,
    resetFiltersChangedSinceLastRequest,
    toggleIncludeNowcasts,
    validateChartFilters,
    validateIncludeNowcastsComplexityLimit,
    validatePreservedChartFilters,
    validateSelectedConditionsComplexityLimit,
    validateSelectedRefinementFiltersComplexityLimit,
} from './actions';
import { filtersChangedSinceLastRequestActions } from './filters-changed-actions';
import {
    applyDefaultFiltersFallback,
    calculateCombinedFiltersCount,
    getChartFiltersToOmit,
    getChartSelectedRefinementFilters,
    getDataToolDataCollections,
    getDataToolDefaultMapType,
    getDefaultFiltersByConditions,
    getIncludeNowcasts,
    getIsNowcastsActive,
    getIsNowcastsActiveWhenIncluded,
    getIsSingleYearAgeDataSuiteSupported,
    getMultipleValuesQueriedRefinementFilters,
    getNormalizedQueriedRefinementFilters,
    getNormalizedSelectedRefinementFilters,
    getPreservedChartFilters,
    getQueriedConditions,
    getQueriedConditionsRefinedGranularity,
    getQueriedDataSettings,
    getQueriedRefinementFilters,
    getSamplePopulationSize,
    getSelectedChartType,
    getSelectedConditions,
    getSelectedConditionsDataTypes,
    getSelectedConditionsDataTypesGranularity,
    getSelectedConditionsRefinedGranularity,
    getSelectedDataProject,
    getSelectedDataType,
    getSelectedRefinementFilters,
    getSelectedRefinementFiltersWithFallbackToDefaultFilters,
    hasFiltersChangedSinceLastRequest,
} from './selectors';

const NON_PRESERVABLE_REFINEMENT_FILTERS_KEYS_BY_DATA_TOOL = {
    [DATA_EXPLORER_PATH]: [],
    [COVID_19_EXPLORER_PATH]: [],
    [MORBIDITY_EXPLORER_PATH]: [AGE_GROUP_ID_KEY],
};

export const validateSelectedChartFilters: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(isActionOf([queryDataAsync.request, changeChartFilters])),
        switchMap((action) => {
            const state = state$.value;

            const refinementFilters = getNormalizedQueriedRefinementFilters(state);
            if (!refinementFilters) return empty();

            const dataTool = getSelectedDataTool(state);
            const chartType = getSelectedChartType(state);
            const chartFilters = getChartSelectedRefinementFilters(state) ?? {};
            const filtersToOmit = getChartFiltersToOmit(state);

            const allowedFilters = getAllowedChartRefinementFilters(chartType, dataTool).filter(
                (key) => !filtersToOmit.includes(key)
            );

            const filtersConfig = getChartRefinementFiltersConfig(chartType, dataTool);
            const updatedChartFilters = { ...chartFilters };

            const isMultiple = (value) => value?.length > 1;

            Object.entries(refinementFilters).forEach(([key, value]) => {
                if (!allowedFilters.includes(key) || !isMultiple(value)) return;

                if (
                    chartFilters.hasOwnProperty(key) &&
                    isPartOfSelectedRefinementFilters(key, value, refinementFilters)
                ) {
                    updatedChartFilters[key] = chartFilters[key];
                } else {
                    const { selectAll, defaultValue } =
                        filtersConfig?.find(({ key: filterKey }) => key === filterKey) ?? {};
                    updatedChartFilters[key] = selectAll ? value : (defaultValue ?? [value[0]]);
                }
            });

            Object.entries(chartFilters).forEach(([key, value]) => {
                if (
                    refinementFilters.hasOwnProperty(key) &&
                    !isPartOfSelectedRefinementFilters(key, value, refinementFilters)
                ) {
                    updatedChartFilters[key] = [refinementFilters[key][0]];
                }

                if (
                    key === MAP_TYPE_ID_KEY &&
                    value != null &&
                    !refinementFilters[LOCATION_ID_KEY].includes(value)
                ) {
                    updatedChartFilters[key] = getDataToolDefaultMapType(state);
                }
            });

            return of(validateChartFilters(updatedChartFilters));
        })
    );

export const updateDataSelectionOnSelectedDataTypeChange: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(isActionOf([changeSelectedDataType])),
        switchMap((action) => {
            const state = state$.value;

            const selectedDataCollection = getSelectedDataCollectionForDataTool(state);
            const selectedRefinementFilters = getSelectedRefinementFilters(state);
            const selectedDataTool = getSelectedDataTool(state);

            if (selectedDataCollection == null) {
                return empty();
            }

            const selectedDataType = getSelectedDataType(state);
            if (!selectedDataCollection || !selectedDataCollection.datasets) {
                return empty();
            }
            const availableDatasets = selectedDataCollection.datasets.filter(
                (i) => selectedDataType === AllDataTypes || selectedDataType === i.data_type
            );
            const availableConditions = flatten(availableDatasets.map(getConditions));
            const selectedConditions = getSelectedConditions(state);

            // special case in comorbidity to preserve selected conditions
            if (selectedDataTool === MORBIDITY_EXPLORER_PATH) {
                // if no conditions selected do nothing
                if (selectedConditions.length === 0) {
                    return empty();
                }
                // otherwise reselect them from new data type
                // find selected condtions by primary id in dataset conditions
                const newConditions: DataCondition[] = selectedConditions
                    .map(({ primary_entity_id }) =>
                        availableConditions.find(
                            (condition) => condition.primary_entity_id === primary_entity_id
                        )
                    )
                    .filter(Boolean);

                const dataTypeGranularity = getMergedDataCollectionGranularity(
                    selectedDataCollection,
                    [selectedDataType]
                );
                const newRefinementFilters = {
                    ...getDefaultFiltersByConditions(newConditions)(state),
                    ...intersectRefinementFiltersWithGranularity(
                        selectedRefinementFilters,
                        omit(
                            NON_PRESERVABLE_REFINEMENT_FILTERS_KEYS_BY_DATA_TOOL[selectedDataTool],
                            dataTypeGranularity
                        )
                    ),
                };

                return of(
                    changeDataTypeSelection({
                        conditions: newConditions,
                        refinementFilters: newRefinementFilters,
                    })
                );
            }

            // reset selected conditions
            return of(
                changeDataTypeSelection({
                    conditions: [],
                    refinementFilters: null,
                })
            );
        })
    );

export const dataSelectionComplexityValidationFlow: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(
            isActionOf([
                validateSelectedRefinementFiltersComplexityLimit,
                validateSelectedConditionsComplexityLimit,
                validateIncludeNowcastsComplexityLimit,
            ])
        ),
        switchMap((action) => {
            const mapToSuccessAction = (action) => {
                switch (action.type) {
                    case getType(validateSelectedRefinementFiltersComplexityLimit):
                        return changeSelectedRefinementFilters(action.payload);

                    case getType(validateSelectedConditionsComplexityLimit):
                        return changeConditions(action.payload);

                    case getType(validateIncludeNowcastsComplexityLimit):
                        return toggleIncludeNowcasts();

                    default:
                        throw Error('Not possible');
                }
            };

            const state = state$.value;
            const selectedRefinementFilters = getNormalizedSelectedRefinementFilters(state);

            const currentSelectionCount = calculateCombinedFiltersCount(state);
            const currentSelectionWithoutTimeUnitsCount = calculateCombinedFiltersCount(state, {
                ignoreTimeUnits: true,
            });

            if (isEmpty(selectedRefinementFilters)) {
                return of(
                    combinedFiltersCountEvaluated({
                        currentSelectionWithoutTimeUnitsCount,
                        nextSelectionWithoutTimeUnitsCount: 1,
                    }),
                    mapToSuccessAction(action)
                );
            }

            const overrides: CombinedFiltersCountOverrides = {};
            if (isActionOf(validateSelectedRefinementFiltersComplexityLimit, action)) {
                // payload first, defaults after — the order the store applies them
                overrides.filters = applyDefaultFiltersFallback(state, {
                    ...getSelectedRefinementFilters(state),
                    ...action.payload,
                });
            } else if (isActionOf(validateSelectedConditionsComplexityLimit, action)) {
                overrides.conditions = action.payload;
            } else if (isActionOf(validateIncludeNowcastsComplexityLimit, action)) {
                overrides.isNowcastsActive = getIsNowcastsActiveWhenIncluded(
                    state,
                    !getIncludeNowcasts(state)
                );
            }

            const nextSelectionCount = calculateCombinedFiltersCount(state, overrides);
            const nextSelectionWithoutTimeUnitsCount = calculateCombinedFiltersCount(state, {
                ...overrides,
                ignoreTimeUnits: true,
            });

            // the nowcasts checkbox flips under the user's cursor, so it applies up
            // front; its success action is a toggle, so re-dispatching it reverts
            const appliesOptimistically = isActionOf(
                validateIncludeNowcastsComplexityLimit,
                action
            );

            const openConfirmationModal = (modal: LimitModal) =>
                concat(
                    of(
                        ...(appliesOptimistically ? [mapToSuccessAction(action)] : []),
                        openSelectionCountModal(modal)
                    ),
                    action$.pipe(
                        filter(isActionOf([confirmSelectionCountModal, cancelSelectionCountModal])),
                        first(),
                        switchMap((resolution) => {
                            if (isActionOf(cancelSelectionCountModal, resolution)) {
                                return appliesOptimistically ? [mapToSuccessAction(action)] : [];
                            }

                            return [
                                combinedFiltersCountEvaluated({
                                    currentSelectionWithoutTimeUnitsCount,
                                    nextSelectionWithoutTimeUnitsCount,
                                }),
                                ...(appliesOptimistically ? [] : [mapToSuccessAction(action)]),
                            ];
                        })
                    )
                );

            if (
                currentSelectionCount < config.combinedFiltersCountLimit &&
                nextSelectionCount > config.combinedFiltersCountLimit
            ) {
                return openConfirmationModal(LimitModal.dataToolLimitExceeded);
            } else if (
                currentSelectionWithoutTimeUnitsCount <
                    config.combinedFiltersWithoutTimeUnitsCountChartLimit &&
                nextSelectionWithoutTimeUnitsCount >
                    config.combinedFiltersWithoutTimeUnitsCountChartLimit
            ) {
                return openConfirmationModal(LimitModal.chartsLimitExceeded);
            } else if (
                currentSelectionWithoutTimeUnitsCount <
                    config.combinedFiltersWithoutTimeUnitsCountWarning &&
                nextSelectionWithoutTimeUnitsCount >
                    config.combinedFiltersWithoutTimeUnitsCountWarning
            ) {
                return openConfirmationModal(LimitModal.slowPerformanceWarning);
            }

            return of(
                combinedFiltersCountEvaluated({
                    currentSelectionWithoutTimeUnitsCount,
                    nextSelectionWithoutTimeUnitsCount,
                }),
                mapToSuccessAction(action)
            );
        })
    );

// normalization can reorder values, so compare per-key as sets
const areValueMapsEqual = (
    a: DataFilters | DataGranularity | null,
    b: DataFilters | DataGranularity | null
): boolean => {
    const aKeys = Object.keys(a || {});
    const bKeys = Object.keys(b || {});

    return (
        aKeys.length === bKeys.length &&
        aKeys.every((key) => {
            const aValues = a?.[key];
            const bValues = b?.[key];
            if (!Array.isArray(aValues) || !Array.isArray(bValues)) {
                return isEqual(aValues, bValues);
            }
            return isEmpty(xor(aValues, bValues));
        })
    );
};

const areConditionSelectionsEqual = (a: DataCondition[], b: DataCondition[]): boolean =>
    isEmpty(xorWith(isEqual, a, b));

// init and granularity-refresh flows re-dispatch selection actions unchanged
export const resetFiltersChangedFlagWhenSelectionMatchesQueried: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(isActionOf(filtersChangedSinceLastRequestActions)),
        mergeMap(() => {
            const state = state$.value;

            if (!hasFiltersChangedSinceLastRequest(state)) {
                return EMPTY;
            }

            const queriedConditions = getQueriedConditions(state);
            if (isEmpty(queriedConditions)) {
                return EMPTY;
            }

            const queriedDataSettings = getQueriedDataSettings(state);
            const settingsMatchQueried =
                queriedDataSettings != null &&
                queriedDataSettings.isNowcastsActive === getIsNowcastsActive(state) &&
                queriedDataSettings.samplePopulationSize === getSamplePopulationSize(state);

            const selectionMatchesQueried =
                settingsMatchQueried &&
                areConditionSelectionsEqual(getSelectedConditions(state), queriedConditions) &&
                areValueMapsEqual(
                    getNormalizedSelectedRefinementFilters(state),
                    getQueriedRefinementFilters(state)
                ) &&
                areValueMapsEqual(
                    getSelectedConditionsRefinedGranularity(state),
                    getQueriedConditionsRefinedGranularity(state)
                );

            return selectionMatchesQueried ? of(resetFiltersChangedSinceLastRequest()) : EMPTY;
        })
    );

export const postInitializeDataTypeSelection: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(isActionOf(initializeDataTypeSelection)),
        mergeMap((action) => {
            const { conditions = [], refinementFilters: rawRefinementFilters } = action.payload;
            const refinementFilters = rawRefinementFilters || {};
            const currentState = state$.value;
            const defaults = getDefaultFiltersByConditions(conditions)(currentState);

            const newRefinementFilters = {
                ...defaults,
                ...refinementFilters,
            };

            if (conditions.length > 0) {
                return from([
                    fixMissingLoadedRefinementFilters(newRefinementFilters),
                    validateSelectedRefinementFiltersComplexityLimit(newRefinementFilters),
                ]);
            } else {
                return from([validateSelectedRefinementFiltersComplexityLimit(refinementFilters)]);
            }
        })
    );

export const initializeDefaultRefinementFiltersForSingleConditionTool: RootEpic = (
    action$,
    state$
) =>
    action$.pipe(
        filter(isActionOf([initializeDataTypeSelection, changeSelectedDataType])),
        switchMap((action) => {
            const state = state$.value;
            const selectedDataCollection = getSelectedDataCollectionForDataTool(state);

            // if only one condition available select it immediately
            const selectedDataType = getSelectedDataType(state);
            if (!selectedDataCollection || !selectedDataCollection.datasets) {
                return empty();
            }
            const availableDatasets = selectedDataCollection.datasets.filter(
                (i) => selectedDataType === AllDataTypes || selectedDataType === i.data_type
            );
            const availableConditions = flatten(availableDatasets.map(getConditions));
            if (availableConditions.length === 1) {
                return of(
                    changeDataTypeSelectionForSingleConditionTool({
                        conditions: availableConditions,
                        refinementFilters: {
                            ...getDefaultFiltersByConditions(availableConditions)(state),
                            ...getSelectedRefinementFilters(state),
                        },
                    })
                );
            }

            return empty();
        })
    );

export const initializeDefaultRefinementFiltersForMultiConditionsTool: RootEpic = (
    action$,
    state$
) =>
    action$.pipe(
        filter(isActionOf([changeConditions])),
        switchMap((action) => {
            const state = state$.value;
            const selectedConditions = getSelectedConditions(state$.value);
            const selectedRefinementFilters = getSelectedRefinementFilters(state$.value);

            // when conditions selected and no refinementFilters they need to be initialized
            // also round_id will always change on refinementFilters initialization
            if (selectedConditions && !selectedRefinementFilters) {
                return of(
                    initializeDefaultRefinementFilters(
                        getDefaultFiltersByConditions(selectedConditions)(state)
                    )
                );
            }

            return empty();
        })
    );

// DataSelection validation flow
// - should be tracking all changes to conditions and refinementFilters by buffering 2 last state changes
// Invariants
// 1. current conditions should not be empty (except data types that only use refinement filters)
// 2. current refinementFilters should not be empty
// 3. current conditions or/and refinementFilters are changed
// 4. current refinementFilters should not have null values
export const dataSelectionValidationFlow: RootEpic = (action$, state$) => {
    let lastKey: string = '';

    const resetKey$ = action$.pipe(
        filter((action) => {
            return (
                action.type === 'DATA_TYPE_SELECTION_INITIALIZED' ||
                isActionOf([toggleIncludeNowcasts])(action)
            );
        }),
        tap(() => {
            lastKey = '';
        }),
        mergeMap(() => EMPTY)
    );

    const validate$ = action$.pipe(
        filter(isActionOf(dataSelectionValidation.request)),
        withLatestFrom(state$),
        map(([action, state]) => {
            const conditions = getSelectedConditions(state);
            const filters = getSelectedRefinementFiltersWithFallbackToDefaultFilters(state);
            const includeNowcasts = getIncludeNowcasts(state);
            const selectedDataType = getSelectedDataType(state);
            return {
                conditions,
                filters,
                includeNowcasts,
                selectedDataType,
                querySource: action.payload.querySource ?? ANALYTICS_SOURCE_WEB,
            };
        }),
        filter(({ conditions, filters, includeNowcasts, selectedDataType }) => {
            const newKey = JSON.stringify({
                conditions,
                filters,
                includeNowcasts,
                selectedDataType,
            });
            if (newKey === lastKey) {
                return false;
            }
            lastKey = newKey;
            return true;
        }),
        mergeMap(({ conditions, filters, selectedDataType, querySource }) => {
            const isDataTypeWithoutConditions =
                selectedDataType != null &&
                getDataTypesWithoutCondition().includes(selectedDataType);

            if (isEmpty(conditions) && !isDataTypeWithoutConditions) {
                return of(
                    dataSelectionValidation.failure(
                        'conditions are empty; waiting for user to select at least one'
                    )
                );
            }

            if (!filters || isEmpty(filters)) {
                return of(
                    dataSelectionValidation.failure(
                        'refinementFilters are empty or null; waiting for user to pick filters'
                    )
                );
            }

            const granularity = getSelectedConditionsDataTypesGranularity(state$.value);
            const hasNull = hasErrorInRefinementFilters({
                granularity,
                selectedConditions: conditions,
                selectedRefinementFilters: filters,
            });

            if (hasNull) {
                return of(
                    dataSelectionValidation.failure(
                        'one of refinementFilters has null value; user must fix selection'
                    )
                );
            }

            return of(dataSelectionValidation.success(querySource));
        })
    );

    return merge(resetKey$, validate$);
};

export const triggerLoadRefinedGranularity: RootEpic = (action$, state$) =>
    concat(of(changeConditions([])), action$).pipe(
        filter(
            isActionOf([
                changeConditions,
                changeSelectedRefinementFilters,
                initializeDataTypeSelection,
                changeDataTypeSelection,
                changeDataTypeSelectionForSingleConditionTool,
            ])
        ),
        map((action) => ({
            conditions: getSelectedConditions(state$.value),
            conditionsDataTypes: getSelectedConditionsDataTypes(state$.value),
            refinementFilters: getSelectedRefinementFilters(state$.value),
        })),
        bufferCount(2, 1),
        mergeMap(
            ([
                {
                    conditions: prevSelectedConditions,
                    conditionsDataTypes: prevSelectedConditionsDataTypes,
                    refinementFilters: prevSelectedRefinementFilters,
                },
                {
                    conditions: selectedConditions,
                    conditionsDataTypes: selectedConditionsDataTypes,
                    refinementFilters: selectedRefinementFilters,
                },
            ]) => {
                if (!selectedConditions && !selectedRefinementFilters) {
                    return empty();
                }

                // when conditions or round_id has changed we are supposed to get refined granularity
                // refined granularity doesn't have any impact on queried data
                // it only marks unavailable filters (queried data is the same with/without them)
                const conditionsChanged = !isEqual(selectedConditions, prevSelectedConditions);
                const roundIdChanged =
                    selectedRefinementFilters &&
                    selectedRefinementFilters[ROUND_ID_KEY] != null &&
                    selectedRefinementFilters[ROUND_ID_KEY] !==
                        (prevSelectedRefinementFilters || {})[ROUND_ID_KEY];

                const newDataTypeIsAdded =
                    (selectedConditionsDataTypes?.length || 0) >
                    (prevSelectedConditionsDataTypes?.length || 0);

                if (conditionsChanged || roundIdChanged) {
                    return of(getRefinedGranularityAsync.request(newDataTypeIsAdded));
                }

                return empty();
            }
        )
    );

export const validateLoadSingleAgeRefinedGranularity: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(isActionOf([getRefinedGranularityAsync.success])),
        isSingleYearAgeEstimationsEnabled(state$),
        switchMap(() => {
            const state = state$.value;

            if (!getIsSingleYearAgeDataSuiteSupported(state)) {
                return EMPTY;
            }

            const dataProject = getSelectedDataProject(state);
            const isDataProjectSupported = config.singleYearAgeDataProjects.includes(dataProject);
            if (!isDataProjectSupported) return EMPTY;

            const selectedConditionsDataTypes = getSelectedConditionsDataTypes(state);
            const hasSupportedDataType = !isEmpty(
                intersection(selectedConditionsDataTypes, config.singleYearAgeDataTypes)
            );
            if (!hasSupportedDataType) return EMPTY;

            return of(getSingleYearAgeGranularitiesAsync.request());
        })
    );

const isSingleYearAgeEstimationsEnabled = (state$) =>
    switchMap((action: any) => {
        const state = state$.value;
        const organization = getOrganization(state);
        const isExportEnabled = checkIfSingleYearAgeEstimatesExportEnabled(organization);
        if (!isExportEnabled) {
            return empty();
        }

        const selectedConditionDataTypes = getSelectedConditionsDataTypes(state);
        if (isEmpty(selectedConditionDataTypes)) {
            return empty();
        }

        const hasSupportedSelectedDataTypes = !isEmpty(
            intersection(selectedConditionDataTypes, config.singleYearAgeDataTypes)
        );
        if (!hasSupportedSelectedDataTypes) {
            return empty();
        }

        return of(action);
    });

export const patchRiskExposureRefinedMeasureAndMetricFilters: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(isActionOf(getRefinedGranularityAsync.success)),
        switchMap((action) => {
            const state = state$.value;

            const dataTypes = getSelectedConditionsDataTypes(state);

            if (!(dataTypes && dataTypes.includes(RiskExposureRecords))) {
                return empty();
            }

            const selectedRefinementFilters = getSelectedRefinementFilters(state);

            const isValidSelection = (filterKey: DataKey): boolean =>
                selectedRefinementFilters &&
                selectedRefinementFilters[filterKey] &&
                !isEmpty(
                    intersection(selectedRefinementFilters[filterKey], action.payload[filterKey])
                );

            const getInvalidValuesReplacement = (key: DataKey): DataFilters | null =>
                !isValidSelection(key)
                    ? {
                          [key]: action.payload[key],
                      }
                    : null;

            const refinementFilters = {
                ...getInvalidValuesReplacement(MEASURE_ID_KEY),
                ...getInvalidValuesReplacement(METRIC_ID_KEY),
                [RISK_EXPOSURE_ID_KEY]: action.payload[RISK_EXPOSURE_ID_KEY],
            };

            return of(changeSelectedRefinementFilters(refinementFilters));
        })
    );

export const updateDataTypeOnDataCollectionChange: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(isActionOf([changeDataCollection])),
        switchMap((action) => {
            const state = state$.value;

            const selectedDataCollection = getSelectedDataCollectionForDataTool(state);
            if (!selectedDataCollection) {
                return empty();
            }

            const availableDataTypes = getDataCollectionDataTypes(selectedDataCollection);
            if (!(availableDataTypes && availableDataTypes.length)) {
                return empty();
            }

            const dataType = availableDataTypes[0];
            return of(changeSelectedDataType(dataType));
        })
    );

export const setDefaultDataCollectionIfItsAbsent: RootEpic = (action$, state$, dependencies) =>
    action$.pipe(
        filter(isActionOf([setDefaultDataCollection])),
        switchMap(() => {
            const state = state$.value;
            const selectedDataCollection = getSelectedDataCollectionForDataTool(state);
            const dataCollections = getDataToolDataCollections(state);
            const dataTool = getSelectedDataTool(state);

            if (selectedDataCollection) {
                return empty();
            }

            return of(changeDataCollection({ dataCollectionId: dataCollections[0].id, dataTool }));
        })
    );

export const persistChartFiltersOnChartTypeAndDimensionChange: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(isActionOf([changeSelectedChartType, changeChartDimension])),
        mergeMap((action) => {
            const state = state$.value;
            const isChartTypeChanged = action.type === getType(changeSelectedChartType);
            const chartType = isChartTypeChanged ? action.payload : getSelectedChartType(state);
            const preservedChartFilters = getPreservedChartFilters(state);
            const omittedChartFilters = getChartFiltersToOmit(state);
            const dataTool = getSelectedDataTool(state);
            const chartRefinementFiltersConfig = getChartRefinementFiltersConfig(
                chartType,
                dataTool
            );
            const multipleValueFilters = getMultipleValuesQueriedRefinementFilters(state);
            const conditions = getQueriedConditions(state);

            const relevantPreservedFilters = Object.entries(preservedChartFilters).reduce(
                (acc, [filter, value]) => {
                    const filterConfig = chartRefinementFiltersConfig.find(
                        ({ key }) => key === filter
                    );

                    if (filterConfig && !omittedChartFilters.includes(filter)) {
                        const { isMulti, selectAll } = filterConfig;
                        let parsedValue = value.length > 1 && !isMulti ? value.slice(0, 1) : value;
                        if (selectAll) {
                            parsedValue = multipleValueFilters[filter];
                        }

                        acc[filter] = parsedValue;
                    }
                    return acc;
                },
                {}
            );

            const actions: Action[] = [
                applyRelevantPreservedChartFilters(relevantPreservedFilters),
            ];

            if (
                dataTool === HEALTH_THREATS_METRICS_PATH &&
                chartType === TYPE_LINE_CHART &&
                relevantPreservedFilters[PRIMARY_ENTITY_KEY]?.[0]
            ) {
                const match = relevantPreservedFilters[PRIMARY_ENTITY_KEY][0].match(/::(\d+)$/);
                if (match) {
                    const [, id] = match;
                    const primaryEntityId = parseInt(id, 10);

                    if (primaryEntityId === config.allPathogensCauseId) {
                        actions.push(
                            validatePreservedChartFilters({
                                ...preservedChartFilters,
                                [PRIMARY_ENTITY_KEY]: [mapConditionToValue(conditions[0])],
                            })
                        );
                    }
                }
            }

            return from(actions);
        })
    );
